// File: apps/web/src/lib/api/useInFlightImport.ts
// Version: 2026.4.9 | Sprint 7 Wave 3 — In-flight import reservation hook
//
// PATTERN
//
// Mirrors the existing import.ts conventions:
//   • useApiClient().createApi() for the axios instance with Entra JWT
//   • Loading + data state, refresh callback, no error envelope (this hook
//     fails silently — the worst case is the banner doesn't appear, and
//     the server still enforces the reservation on upload)
//
// Three responsibilities:
//   1. On mount, GET /api/v1/import/in-flight and store the result.
//   2. Expose `setInFlightFromConflict` so the upload handler can feed
//      a 409 conflict response straight in without a refetch.
//   3. Expose `cancel()` that calls DELETE and clears local state.
//
// All types come from types-import.ts — this file does NOT redeclare them.

import { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '@forgetrack/api-client';
import type {
  InFlightImportJob,
  ImportInFlightConflict,
  ImportCancelOutcome,
} from './types-import';

interface UseInFlightImportResult {
  inFlight: InFlightImportJob | null;
  loading:  boolean;
  cancel:   () => Promise<ImportCancelOutcome>;
  /**
   * Optimistically populate from a 409 conflict response, then refetch
   * the full record so the banner has accurate timestamps.
   */
  setInFlightFromConflict: (conflict: ImportInFlightConflict) => Promise<void>;
  refresh:  () => Promise<void>;
}

export function useInFlightImport(): UseInFlightImportResult {
  const { createApi } = useApiClient();
  const [inFlight, setInFlight] = useState<InFlightImportJob | null>(null);
  const [loading,  setLoading]  = useState<boolean>(true);

  const refresh = useCallback(async () => {
    try {
      const api = await createApi();
      const response = await api.get<InFlightImportJob | ''>('/api/v1/import/in-flight', {
        // 204 has no body; axios will give us an empty string. We treat
        // both 204 and 200 as success and let the body shape decide.
        validateStatus: (s) => s === 200 || s === 204,
      });

      if (response.status === 204 || !response.data) {
        setInFlight(null);
      } else {
        setInFlight(response.data as InFlightImportJob);
      }
    } catch (err) {
      // Don't blow up the page if this one call fails. The worst case is
      // the banner doesn't appear — the server still enforces the
      // reservation on upload, so the user can't actually double-submit.
      console.error('Failed to fetch in-flight import', err);
      setInFlight(null);
    } finally {
      setLoading(false);
    }
  }, [createApi]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setInFlightFromConflict = useCallback(
    async (conflict: ImportInFlightConflict) => {
      // Optimistically populate so the banner appears instantly.
      setInFlight({
        batchId:         conflict.batchId,
        state:           conflict.state,
        initiatorUserId: 0, // unknown until refresh
        createdAt:       new Date().toISOString(),
        startedAt:       null,
        lastHeartbeatAt: new Date().toISOString(),
      });
      // Then fetch the full record so the banner has accurate timestamps
      // and the real initiator user id.
      await refresh();
    },
    [refresh],
  );

  const cancel = useCallback(async (): Promise<ImportCancelOutcome> => {
    if (!inFlight) return 'error';

    try {
      const api = await createApi();
      const response = await api.delete(
        `/api/v1/import/in-flight/${encodeURIComponent(inFlight.batchId)}`,
        {
          // Don't throw on 404/409 — they're business outcomes we want to
          // inspect, not network errors.
          validateStatus: (s) => s === 200 || s === 404 || s === 409,
        },
      );

      if (response.status === 200) {
        setInFlight(null);
        return 'cancelled';
      }

      if (response.status === 404) {
        // Job vanished between load and click. Refresh and clear.
        await refresh();
        return 'not-found';
      }

      // 409 — already terminal (completed/failed/cancelled by something else).
      await refresh();
      return 'already-terminal';
    } catch (err) {
      console.error('Failed to cancel in-flight import', err);
      return 'error';
    }
  }, [inFlight, createApi, refresh]);

  return { inFlight, loading, cancel, setInFlightFromConflict, refresh };
}
